import { supabase } from './supabase';
import type { Database } from '../types/database.types';
import { computeMarcaDaBollo } from './noVatClause';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];
type InvoiceInsert = Database['public']['Tables']['invoices']['Insert'];
type InvoiceItemInsert = Database['public']['Tables']['invoice_items']['Insert'];

export type InvoiceStatus = 'draft' | 'issued' | 'cancelled';
export type DocumentType = 'invoice' | 'invoice_with_delivery' | 'proforma' | 'ddt' | 'credit_note';

export const getDocumentTypeLabel = (type: DocumentType | string): string => {
  switch (type) {
    case 'invoice':
      return 'Fattura';
    case 'invoice_with_delivery':
      return 'Fattura Accompagnatoria';
    case 'proforma':
      return 'Fattura Pro Forma';
    case 'ddt':
      return 'DDT';
    case 'credit_note':
      return 'Nota di Credito';
    default:
      return String(type);
  }
};

export interface InvoiceItemInput {
  product_id: string | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

export interface InvoicePayload {
  customer_id: string;
  order_id?: string | null;
  invoice_date: string; // ISO yyyy-mm-dd
  payment_terms?: string | null;
  notes?: string | null;
  status: InvoiceStatus;
  document_type: DocumentType;
  items: InvoiceItemInput[];
  // Regime forfettario (assenza IVA L. 190/2014)
  no_vat_forfettario?: boolean;
  no_vat_clause_text?: string | null;
  iban?: string | null;
  causale?: string | null;
  // Campi specifici per DDT e Fattura Accompagnatoria
  carrier_id?: string | null;
  recipient_name?: string | null;
  recipient_address?: string | null;
  destination_address?: string | null;
  transport_date?: string | null; // ISO yyyy-mm-dd
  numero_colli?: number | null;
  package_type_id?: string | null;
  peso?: number | null;
  // Campi specifici per Nota di Credito
  reference_invoice_id?: string | null;
  credit_note_reason?: string | null;
  // Tracciabilità Offerta → Fattura e Acconto → Saldo
  price_list_id?: string | null;
  parent_invoice_id?: string | null;
  deposit_percentage?: number | null;
  // Tracciabilità Abbonamento → Fattura (un solo documento per periodo)
  subscription_id?: string | null;
  subscription_period_start?: string | null;
  subscription_period_end?: string | null;
}

export interface CalculatedInvoiceTotals {
  subtotal_amount: number;
  discount_amount: number;
  taxable_amount: number;
  tax_amount: number;
  total_amount: number;
  marca_da_bollo_amount: number;
  items: Omit<InvoiceItemInsert, 'invoice_id'>[];
}

export const calculateInvoiceTotals = (
  items: InvoiceItemInput[],
  noVatForfettario: boolean = false
): CalculatedInvoiceTotals => {
  let subtotal = 0;
  let discount = 0;
  let taxable = 0;
  let tax = 0;

  const mappedItems: Omit<InvoiceItemInsert, 'invoice_id'>[] = items.map((item) => {
    const effectiveVatRate = noVatForfettario ? 0 : item.vat_rate;
    const rowSubtotal = item.quantity * item.unit_price;
    const rowDiscount = rowSubtotal * (item.discount_percentage / 100);
    const rowTaxable = rowSubtotal - rowDiscount;
    const rowTax = rowTaxable * (effectiveVatRate / 100);
    const rowTotal = rowTaxable + rowTax;

    subtotal += rowSubtotal;
    discount += rowDiscount;
    taxable += rowTaxable;
    tax += rowTax;

    return {
      product_id: item.product_id,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: effectiveVatRate,
      vat_amount: rowTax,
      line_total: rowTotal,
    };
  });

  const marcaDaBollo = computeMarcaDaBollo(taxable, noVatForfettario);

  return {
    subtotal_amount: Number(subtotal.toFixed(2)),
    discount_amount: Number(discount.toFixed(2)),
    taxable_amount: Number(taxable.toFixed(2)),
    tax_amount: Number(tax.toFixed(2)),
    total_amount: Number((taxable + tax + marcaDaBollo).toFixed(2)),
    marca_da_bollo_amount: marcaDaBollo,
    items: mappedItems,
  };
};

/**
 * Mappa il tipo di documento al prefisso per la numerazione
 */
const getDocumentPrefix = (documentType: DocumentType): string => {
  switch (documentType) {
    case 'invoice':
      return 'F'; // Fattura normale
    case 'invoice_with_delivery':
      return 'FA'; // Fattura accompagnatoria
    case 'proforma':
      return 'PF'; // Fattura pro forma
    case 'ddt':
      return 'DDT'; // Documento di Trasporto
    case 'credit_note':
      return 'NC'; // Nota di credito
    default:
      return 'F';
  }
};

/**
 * Genera il numero documento chiamando la RPC `next_invoice_number`.
 * Richiede che la funzione esista già nel database.
 */
export const generateInvoiceNumber = async (
  invoiceDate: string,
  documentType: DocumentType = 'invoice'
): Promise<string | null> => {
  const year = new Date(invoiceDate).getFullYear();
  const prefix = getDocumentPrefix(documentType);

  const { data, error } = await supabase.rpc('next_invoice_number', {
    p_year: year,
    p_prefix: prefix,
  });

  if (error) {
    console.error('Errore generazione numero documento:', error);
    // In caso di errore, restituiamo null così il documento rimane senza numero
    return null;
  }

  return data as string;
};

export const createInvoice = async (payload: InvoicePayload): Promise<InvoiceRow> => {
  if (!payload.items || payload.items.length === 0) {
    throw new Error('Il documento deve contenere almeno una riga.');
  }

  // Recupera l'utente corrente
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Utente non autenticato. Devi essere loggato per creare documenti.');
  }

  // Default forfettario = true (ditta individuale forfettaria). Disattivabile esplicitamente.
  const noVatForfettario = payload.no_vat_forfettario !== false;
  const { items, ...totals } = calculateInvoiceTotals(payload.items, noVatForfettario);

  let invoiceNumber: string | null = null;
  if (payload.status === 'issued') {
    invoiceNumber = await generateInvoiceNumber(payload.invoice_date, payload.document_type);
  }

  const insertData = {
    customer_id: payload.customer_id,
    order_id: payload.order_id ?? null,
    invoice_date: payload.invoice_date,
    payment_terms: payload.payment_terms ?? null,
    payment_due_date: null,
    payment_status: 'pending',
    status: payload.status,
    document_type: payload.document_type,
    notes: payload.notes ?? null,
    invoice_number: invoiceNumber ?? undefined,
    subtotal_amount: totals.subtotal_amount,
    discount_amount: totals.discount_amount,
    taxable_amount: totals.taxable_amount,
    tax_amount: totals.tax_amount,
    total_amount: totals.total_amount,
    currency: 'EUR',
    no_vat_forfettario: noVatForfettario,
    no_vat_clause_text: payload.no_vat_clause_text ?? null,
    marca_da_bollo_amount: totals.marca_da_bollo_amount,
    iban: payload.iban ?? null,
    causale: payload.causale ?? null,
    // Campi specifici per DDT e Fattura Accompagnatoria
    carrier_id: payload.carrier_id ?? null,
    recipient_name: payload.recipient_name ?? null,
    recipient_address: payload.recipient_address ?? null,
    destination_address: payload.destination_address ?? null,
            transport_date: payload.transport_date ?? null,
            numero_colli: payload.numero_colli ?? null,
            package_type_id: payload.package_type_id ?? null,
            peso: payload.peso ?? null,
    // Campi specifici per Nota di Credito
    reference_invoice_id: payload.reference_invoice_id ?? null,
    credit_note_reason: payload.credit_note_reason ?? null,
    // Tracciabilità Offerta → Fattura e Acconto → Saldo
    price_list_id: payload.price_list_id ?? null,
    parent_invoice_id: payload.parent_invoice_id ?? null,
    deposit_percentage: payload.deposit_percentage ?? null,
    // Tracciabilità Abbonamento → Fattura
    subscription_id: payload.subscription_id ?? null,
    subscription_period_start: payload.subscription_period_start ?? null,
    subscription_period_end: payload.subscription_period_end ?? null,
    created_at: undefined,
    updated_at: undefined,
    created_by: user.id,
    id: undefined as any,
  } as any;

  let { data: invoice, error: invoiceError } = await supabase
    .from('invoices')
    .insert(insertData)
    .select('*')
    .single();

  // Fallback: se la colonna document_type non esiste, riprova senza
  if (invoiceError) {
    const err = invoiceError as any;
    if (
      err?.code === 'PGRST204' ||
      String(err?.message || '').toLowerCase().includes('document_type') ||
      String(err?.message || '').includes('column') && String(err?.message || '').includes('document_type')
    ) {
      console.warn('Campo document_type non trovato nel database, tentativo senza campo...');
      const { document_type, ...fallbackData } = insertData;
      const result = await supabase
        .from('invoices')
        .insert(fallbackData)
        .select('*')
        .single();
      
      if (result.error) {
        console.error('Errore creazione fattura (anche senza document_type):', result.error);
        throw new Error(
          'Errore creazione documento. Assicurati di aver eseguito lo script SQL ADD_DOCUMENT_TYPES_TO_INVOICES.sql sul database. Dettagli: ' + 
          (result.error.message || 'Errore sconosciuto')
        );
      }
      invoice = result.data;
      invoiceError = null;
    } else {
      console.error('Errore creazione fattura:', invoiceError);
      throw invoiceError;
    }
  }

  if (!invoice) {
    throw new Error('Errore creazione documento: nessun dato restituito');
  }

  // Prepara gli item per l'inserimento, includendo solo i campi necessari
  const itemsWithInvoice: InvoiceItemInsert[] = items.map((item) => {
    const cleanItem: any = {
      invoice_id: invoice.id,
      product_id: item.product_id ?? null,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit ?? null,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: item.vat_rate,
      vat_amount: item.vat_amount,
      line_total: item.line_total,
    };
    
    // Includi order_item_id solo se presente (è opzionale)
    if (item.order_item_id !== null && item.order_item_id !== undefined) {
      cleanItem.order_item_id = item.order_item_id;
    }
    
    return cleanItem as InvoiceItemInsert;
  });

  const { error: itemsError } = await supabase
    .from('invoice_items')
    .insert(itemsWithInvoice);

  if (itemsError) {
    console.error('Errore creazione righe fattura:', itemsError);
    throw itemsError;
  }

  return invoice;
};

/**
 * Emette una bozza: assegna il numero progressivo e passa lo stato a `issued`.
 * L'invio allo SDI resta un passaggio separato e manuale.
 */
export const issueDraftInvoice = async (invoiceId: string): Promise<InvoiceRow> => {
  const { data: current, error: fetchError } = await supabase
    .from('invoices')
    .select('id, status, invoice_date, document_type, invoice_number')
    .eq('id', invoiceId)
    .single();

  if (fetchError) throw fetchError;
  if (!current) throw new Error('Documento non trovato.');

  const invoice = current as Pick<InvoiceRow, 'id' | 'status' | 'invoice_date' | 'document_type' | 'invoice_number'>;

  if (invoice.status !== 'draft') {
    throw new Error('Solo le bozze possono essere emesse.');
  }

  const invoiceNumber = await generateInvoiceNumber(
    invoice.invoice_date,
    (invoice.document_type as DocumentType) ?? 'invoice'
  );

  if (!invoiceNumber) {
    throw new Error('Numerazione non riuscita: il documento resta in bozza.');
  }

  const { data: updated, error: updateError } = await supabase
    .from('invoices')
    .update({
      status: 'issued',
      invoice_number: invoiceNumber,
      updated_at: new Date().toISOString(),
    } as any)
    .eq('id', invoiceId)
    .eq('status', 'draft') // corsa fra due schede: la seconda non sovrascrive
    .select('*')
    .maybeSingle();

  if (updateError) throw updateError;
  if (!updated) throw new Error('Il documento è già stato emesso da un altro utente.');

  return updated as InvoiceRow;
};




